import React, { useState, useEffect } from 'react';
import dataManager from '../utils/dataManager';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/appConfig';
import { getAuthHeaders } from '../utils/authApi';
import ConfigService from '../utils/configService';

const AddReflection = ({ onSubmit, currentUser }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    feeling: 'peaceful',
    energy: 5,
    focus: 5,
    flexibility: 5,
    teacher: '',
    content: '', // 体式反思内容
    isPublic: true
  });

  // 选中的体式列表（多选）
  const [selectedPoses, setSelectedPoses] = useState([]);

  // 体式数据状态
  const [poseCategories, setPoseCategories] = useState({});
  const [philosophies, setPhilosophies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // 加载体式和哲学数据
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 并行加载体式数据和哲学数据
        const [poses, philosophiesData] = await Promise.all([
          dataManager.getPoses(),
          dataManager.getPracticePhilosophy()
        ]);

        setPoseCategories(poses && typeof poses === 'object' ? poses : {});
        setPhilosophies(Array.isArray(philosophiesData) ? philosophiesData : []);

      } catch (err) {
        console.error('Error loading form data:', err);
        setError('加载表单数据失败，请确保Django服务器正在运行');
        // 在服务器模式下不降级，保持空数据
        setPoseCategories({});
        setPhilosophies([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // 从数据管理器中获取数据
  const feelingOptions = dataManager.getFeelingOptions().map(option => ({
    value: option.id,
    label: option.name,
    emoji: option.emoji
  }));

  const allPoses = Object.values(poseCategories).flat();

  const getPoseName = (pose) => {
    if (typeof pose === 'string') return pose;
    return pose?.chineseName || pose?.chinese_name || pose?.name || '';
  };

  const getErrorMessage = (data, fallback) => {
    if (!data || typeof data !== 'object') return fallback;
    if (data.detail) return data.detail;
    if (data.non_field_errors?.[0]) return data.non_field_errors[0];

    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue[0]) {
      return `${firstKey}: ${firstValue[0]}`;
    }
    return fallback;
  };

  // 切换体式选中状态
  const togglePose = (poseName) => {
    setSelectedPoses(prev => {
      if (prev.includes(poseName)) {
        return prev.filter(p => p !== poseName);
      } else {
        return [...prev, poseName];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    // 验证至少选择了一个体式
    if (selectedPoses.length === 0) {
      alert('请至少选择一个体式！');
      return;
    }

    // 验证反思内容
    if (!formData.content.trim()) {
      alert('请输入体式反思内容！');
      return;
    }

    const selectedPoseIds = selectedPoses
      .map((poseName) => allPoses.find((pose) => getPoseName(pose) === poseName)?.id)
      .filter(Boolean);

    if (ConfigService.useServerData() && selectedPoseIds.length !== selectedPoses.length) {
      alert('部分体式没有找到对应ID，请刷新页面后重试。');
      return;
    }

    // 准备提交数据
    const submissionData = {
      date: formData.date,
      time: formData.time,
      feeling: formData.feeling,
      energy_level: formData.energy,
      focus_level: formData.focus,
      flexibility_level: formData.flexibility,
      content: formData.content,
      teacher_id: formData.teacher ? null : null, // 如果有teacher字段需要处理
      is_public: formData.isPublic,
      focused_pose_ids: selectedPoseIds
    };

    console.log('提交数据:', submissionData);
    console.log('选中的体式:', selectedPoses);

    try {
      setSubmitting(true);

      let savedReflection;
      if (ConfigService.useServerData()) {
        const response = await fetch(ConfigService.getApiUrl(API_ENDPOINTS.REFLECTIONS), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(submissionData)
        });
        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(getErrorMessage(responseData, '保存体式反思失败'));
        }

        savedReflection = responseData;
        dataManager.invalidateCache('practiceReflections');
      } else {
        const existingReflections = JSON.parse(localStorage.getItem('userReflections') || '[]');
        savedReflection = {
          id: Date.now(),
          ...submissionData,
          focusedPoses: selectedPoses,
          user: user.username,
          userId: user.id,
          createdAt: new Date().toISOString()
        };
        existingReflections.push(savedReflection);
        localStorage.setItem('userReflections', JSON.stringify(existingReflections));
      }

      alert(`体式反思记录提交成功！共记录了 ${selectedPoses.length} 个体式。`);

      // 重置表单
      setFormData({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        feeling: 'peaceful',
        energy: 5,
        focus: 5,
        flexibility: 5,
        teacher: '',
        content: '',
        isPublic: true
      });
      setSelectedPoses([]);

      if (typeof onSubmit === 'function') {
        onSubmit(savedReflection);
      }
    } catch (err) {
      console.error('Error saving reflection:', err);
      alert(err.message || '保存体式反思失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 如果用户未登录，显示提示信息
  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="flex justify-center mb-4">
            <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-yellow-800 mb-2">需要登录才能添加体式反思</h3>
          <p className="text-yellow-700 mb-4">请先登录您的账号，然后就可以分享您的瑜伽练习体验了。</p>
          <p className="text-sm text-yellow-600">点击右上角的登录按钮开始吧！</p>
        </div>
      </div>
    );
  }


  // 加载状态
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-emerald-100/50 p-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white text-2xl">🧘‍♀️</span>
          </div>
          <p className="text-emerald-600">正在加载表单数据...</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-red-100/50 p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">⚠️</span>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl border border-emerald-100/50 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">添加体式反思</h1>
          <p className="text-emerald-100">记录你的瑜伽练习体验和感悟</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
          {/* 基本信息 - 简化布局 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-emerald-800 mb-2">
                练习日期
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className="w-full px-4 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-emerald-800 mb-2">
                练习时间
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange('time', e.target.value)}
                className="w-full px-4 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          <div>
            <label className="block text-sm font-medium text-emerald-800 mb-2">
              指导老师（可选）
            </label>
            <input
              type="text"
              value={formData.teacher}
              onChange={(e) => handleInputChange('teacher', e.target.value)}
              placeholder="输入老师姓名"
                className="w-full px-4 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            </div>
          </div>

          {/* 体式选择 - 多选标签形式 */}
          <div>
            <label className="block text-sm font-medium text-emerald-800 mb-3">
              选择练习的体式 <span className="text-emerald-600">（可多选）</span>
              {selectedPoses.length > 0 && (
                <span className="ml-2 text-xs text-emerald-600">已选择 {selectedPoses.length} 个</span>
              )}
            </label>
            <div className="space-y-4 max-h-96 overflow-y-auto border border-emerald-100 rounded-lg p-4 bg-emerald-50/30">
              {Object.entries(poseCategories).map(([category, poses]) => (
                <div key={category}>
                  <h4 className="text-sm font-semibold text-emerald-700 mb-2 sticky top-0 bg-emerald-50/80 py-1">
                    {category}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {poses.map((pose) => {
                      const poseName = getPoseName(pose);
                      if (!poseName) return null;
                      const isSelected = selectedPoses.includes(poseName);
                      return (
                        <button
                          key={poseName}
                          type="button"
                          onClick={() => togglePose(poseName)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isSelected
                              ? 'bg-emerald-500 text-white shadow-md transform scale-105'
                              : 'bg-white text-emerald-700 border border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50'
                          }`}
                        >
                          {poseName}
                          {isSelected && <span className="ml-1">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 体式反思内容 */}
          <div>
            <label className="block text-sm font-medium text-emerald-800 mb-2">
              体式反思 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              placeholder="分享你对这些体式的练习感受、体悟或遇到的挑战..."
              rows={6}
              className="w-full px-4 py-3 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            />
            <p className="text-xs text-emerald-600 mt-1">写下你对所选体式的整体反思和体验</p>
          </div>

          {/* 快速设置 - 折叠式 */}
          <details className="border border-emerald-200 rounded-lg">
            <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-emerald-800 hover:bg-emerald-50">
              ⚙️ 更多设置（可选）
            </summary>
            <div className="p-4 space-y-4 border-t border-emerald-100">
              {/* 感受选择 */}
              <div>
                <label className="block text-sm font-medium text-emerald-800 mb-2">
                  整体感受
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {feelingOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleInputChange('feeling', option.value)}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                    formData.feeling === option.value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-emerald-200 hover:border-emerald-300 text-emerald-600'
                  }`}
                >
                      <div className="text-xl mb-1">{option.emoji}</div>
                      <div className="text-xs font-medium">{option.label}</div>
                </button>
              ))}
            </div>
          </div>

              {/* 评分 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: 'energy', label: '能量水平', color: 'emerald' },
              { key: 'focus', label: '专注程度', color: 'amber' },
              { key: 'flexibility', label: '柔韧性', color: 'teal' }
            ].map((rating) => (
              <div key={rating.key}>
                    <label className="block text-sm font-medium text-emerald-800 mb-2">
                  {rating.label}: {formData[rating.key]}/10
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={formData[rating.key]}
                  onChange={(e) => handleInputChange(rating.key, parseInt(e.target.value))}
                      className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-emerald-600 mt-1">
                  <span>1</span>
                  <span>5</span>
                  <span>10</span>
                </div>
              </div>
            ))}
          </div>
            </div>
          </details>

          <label className="flex items-start gap-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-4 text-sm text-emerald-800">
            <input
              type="checkbox"
              checked={formData.isPublic}
              onChange={(e) => handleInputChange('isPublic', e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span>
              <span className="font-semibold">公开给其他人查看</span>
              <span className="block text-emerald-600">关闭后只会出现在“我的帖子”里。</span>
            </span>
          </label>

          {/* Submit Button */}
          <div className="flex justify-between items-center pt-4 border-t border-emerald-100">
            <div className="text-sm text-emerald-600">
              {selectedPoses.length > 0 && (
                <span>已选择 {selectedPoses.length} 个体式</span>
              )}
            </div>
            <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => window.history.back()}
                className="px-6 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors duration-200"
            >
              取消
            </button>
            <button
              type="submit"
                disabled={submitting || selectedPoses.length === 0 || !formData.content.trim()}
                className="px-8 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {submitting ? '保存中...' : '保存体式反思'}
            </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddReflection;
